import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createServices } from 'api/Service';

import { usePracticeCPTCodes } from 'lib/hooks/queries/cptCodes/useCPTCodes';
import { useProfessionGroups } from 'lib/hooks/queries/specialty/useProfessionGroups';

const ALL_SERVICES_TAB = { value: 'all', label: 'All services' };
const MIN_ITEMS_FOR_CONTROLS = 10;
const DEFAULT_LIMIT = 50;

export const useAddServicesModal = (onServicesAdded, onClose) => {
  const navigate = useNavigate();
  const { id: practiceId } = useParams();
  const [selectedRows, setSelectedRows] = useState([]);
  const [standardChargeMultiplier, setStandardChargeMultiplier] = useState(100);
  const [selfPayMultiplier, setSelfPayMultiplier] = useState(100);
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [gridApi, setGridApi] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [filters, setFilters] = useState({});
  const [localEdits, setLocalEdits] = useState({});
  const [shouldShowControls, setShouldShowControls] = useState(false);

  const { data: professionGroupsData } = useProfessionGroups();
  const specialties = professionGroupsData?.specialties || [];

  const specialtyFilter = useMemo(() => {
    if (selectedSpecialty === 'all') return undefined;
    return [{ values: Number(selectedSpecialty) }];
  }, [selectedSpecialty]);

  const apiFilters = useMemo(() => {
    const result = {};
    if (specialtyFilter) {
      result.professionGroup = specialtyFilter;
    }
    if (filters.name?.length > 0) {
      result.name = filters.name;
    }
    return Object.keys(result).length > 0 ? result : undefined;
  }, [specialtyFilter, filters.name]);

  const {
    data: cptData,
    refetch: refetchCptCodes,
    isFetching
  } = usePracticeCPTCodes({
    params: {
      forPractice: false,
      getPayers: true,
      practiceId: Number(practiceId),
      page,
      limit,
      withCount: true,
      ...(apiFilters && { filters: apiFilters })
    },
    dependencies: [page, limit, selectedSpecialty, JSON.stringify(filters.name)],
    options: { keepPreviousData: true }
  });

  const tabsData = useMemo(() => {
    const tabs = [ALL_SERVICES_TAB];
    specialties.forEach((specialty) => {
      tabs.push({ value: String(specialty.id), label: specialty.name });
    });
    return tabs;
  }, [specialties]);

  const filteredData = useMemo(() => {
    if (!cptData?.cpt) return [];

    const standardPercentage = parseFloat(standardChargeMultiplier || 100) / 100;
    const selfPayPercentage = parseFloat(selfPayMultiplier || 100) / 100;

    return cptData.cpt
      .filter((item) => (item.name || item.cpt_description || '').trim() !== '')
      .map((item) => {
        const edits = localEdits[item.code] || {};
        const merged = { ...item, ...edits };
        const effectiveParAmount = edits.par_amount ?? item.par_amount;
        return {
          ...merged,
          standard_charge: Number(effectiveParAmount * standardPercentage),
          self_pay: Number(effectiveParAmount * selfPayPercentage),
          total_cost_cents: Number((effectiveParAmount * standardPercentage).toFixed(0))
        };
      });
  }, [cptData?.cpt, localEdits, standardChargeMultiplier, selfPayMultiplier]);

  const paginatedAndFilteredData = useMemo(() => {
    let result = [...filteredData];
    if (filters.self_pay?.length > 0) {
      filters.self_pay.forEach((filter) => {
        const value = parseFloat(filter.values);
        if (!isNaN(value)) {
          result = result.filter((item) => {
            const itemValue = parseFloat(item.self_pay) || 0;
            if (filter.formula === 'is equal to') return itemValue === value;
            if (filter.formula === 'is less than') return itemValue < value;
            if (filter.formula === 'is more than') return itemValue > value;
            return true;
          });
        }
      });
    }
    return result;
  }, [filteredData, filters.self_pay]);

  const totalItems = cptData?.count ?? 0;
  const hasFiltersApplied = Object.keys(filters || {}).length > 0;
  const showControls = shouldShowControls || totalItems >= MIN_ITEMS_FOR_CONTROLS || page > 1;

  const queryClient = useQueryClient();

  const { mutateAsync: createServicesHandler, isLoading } = useMutation({
    mutationFn: (params) => createServices(navigate, params),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['services'] })
  });

  const onSelectionChanged = useCallback((gridRef) => {
    setSelectedRows(gridRef?.api.getSelectedRows() ?? []);
  }, []);

  useEffect(() => {
    setSelectedRows((prevSelectedRows) => {
      if (prevSelectedRows.length === 0) return prevSelectedRows;
      const selectedCodes = new Set(prevSelectedRows.map((row) => row.code));
      return filteredData.filter((row) => selectedCodes.has(row.code));
    });
  }, [filteredData]);

  const handleStandardChargeChange = (e) => {
    setStandardChargeMultiplier(e.target.value);
  };

  const handleSelfPayChange = (e) => {
    setSelfPayMultiplier(e.target.value);
  };

  const handleAddServices = async () => {
    if (selectedRows.length > 0) {
      const servicesWithEdits = selectedRows.map((row) => ({
        ...row,
        ...(localEdits[row.code] || {})
      }));
      await createServicesHandler({
        services: servicesWithEdits,
        practiceId: Number(practiceId)
      });
      onServicesAdded?.();
      onClose();
    }
  };

  const handleCreateNewService = async (newService) => {
    await createServicesHandler({
      services: [newService],
      practiceId: Number(practiceId)
    });
    refetchCptCodes();
  };

  const handleSpecialtyChange = useCallback(
    (specialty) => {
      setSelectedSpecialty(specialty);
      setPage(1);
    },
    []
  );

  const handleLimitChange = useCallback(
    (newLimit) => {
      setLimit(newLimit);
      setPage(1);
    },
    []
  );

  const changeFilters = useCallback((updater) => {
    setFilters((prevFilters) => {
      const currentFilters = prevFilters || {};
      const newFilters = typeof updater === 'function' ? updater(currentFilters) : updater;
      return newFilters || {};
    });
    setPage(1);
  }, []);

  useEffect(() => {
    if (!hasFiltersApplied && totalItems >= MIN_ITEMS_FOR_CONTROLS) {
      setShouldShowControls(true);
    }
  }, [totalItems, hasFiltersApplied]);

  return {
    selectedRows,
    standardChargeMultiplier,
    selfPayMultiplier,
    selectedSpecialty,
    gridApi,
    page,
    limit,
    filters,
    localEdits,
    shouldShowControls,
    tabsData,
    paginatedAndFilteredData,
    totalItems,
    showControls,
    isLoading,
    isFetching,
    setSelectedSpecialty: handleSpecialtyChange,
    setGridApi,
    setPage,
    setLimit: handleLimitChange,
    setLocalEdits,
    changeFilters,
    handleStandardChargeChange,
    handleSelfPayChange,
    handleAddServices,
    handleCreateNewService,
    onSelectionChanged,
    refetchCptCodes
  };
};
